import { useState } from 'react'
import { Copy, Key, Plus } from 'lucide-react'
import type { ColumnDef } from '@tanstack/react-table'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { DataTable } from '@/components/DataTable'
import { Modal } from '@/components/Modal'
import { Toolbar } from '@/components/Toolbar'
import { FormField } from '@/components/FormField'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { useToast } from '@/hooks/useToast'
import { useAuth } from '@/auth/useAuth'
import { ApiError } from '@/lib/api'
import {
  useApiKeys,
  useCreateApiKey,
  useDeleteApiKey,
} from '@/features/team/queries'
import type { ApiKey } from '@/features/team/types'

const errMsg = (err: unknown, fallback = 'Action failed.') => {
  if (err instanceof ApiError && err.body && typeof err.body === 'object' && 'error' in err.body) {
    const m = (err.body as { error: unknown }).error
    if (typeof m === 'string' && m) return m
  }
  if (err instanceof Error && err.message) return err.message
  return fallback
}

const fmtDate = (v: string | null) => (v ? new Date(v).toLocaleString() : '-')

export function ApiKeysTab() {
  const { hasPermission } = useAuth()
  const { toast } = useToast()
  const keysQ = useApiKeys()
  const create = useCreateApiKey()
  const remove = useDeleteApiKey()
  const canManage = hasPermission('api_keys.manage')

  const [open, setOpen] = useState(false)
  const [name, setName] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [secret, setSecret] = useState<string | null>(null)
  const [confirmDel, setConfirmDel] = useState<ApiKey | null>(null)

  const submit = async () => {
    setError(null)
    if (!name.trim()) {
      setError('Name is required.')
      return
    }
    try {
      const r = await create.mutateAsync({ name: name.trim() })
      setSecret(r.apiKey.secret)
      toast({ title: 'API key created.' })
    } catch (err) {
      setError(errMsg(err))
    }
  }

  const reset = () => {
    setName('')
    setError(null)
    setSecret(null)
  }

  const onDelete = async (k: ApiKey) => {
    try {
      await remove.mutateAsync(k.id)
      toast({ title: 'API key revoked.' })
    } catch (err) {
      toast({ variant: 'destructive', title: 'Failed', description: errMsg(err) })
    }
  }

  const copySecret = async () => {
    if (!secret) return
    try {
      await navigator.clipboard.writeText(secret)
      toast({ title: 'Copied to clipboard.' })
    } catch {
      toast({ variant: 'destructive', title: 'Copy failed', description: 'Select the text manually.' })
    }
  }

  const keys = keysQ.data?.apiKeys ?? []

  const columns: ColumnDef<ApiKey>[] = [
    {
      accessorKey: 'name',
      header: 'Name',
      cell: ({ row }) => <span className="font-semibold">{row.original.name}</span>,
    },
    {
      accessorKey: 'prefix',
      header: 'Prefix',
      cell: ({ row }) => <code className="font-mono text-xs">{row.original.prefix}…</code>,
    },
    {
      accessorKey: 'created_at',
      header: 'Created',
      cell: ({ row }) => <span className="text-xs text-muted-foreground">{fmtDate(row.original.created_at)}</span>,
    },
    {
      accessorKey: 'last_used_at',
      header: 'Last used',
      cell: ({ row }) => <span className="text-xs text-muted-foreground">{fmtDate(row.original.last_used_at)}</span>,
    },
    {
      id: 'actions',
      header: '',
      cell: ({ row }) =>
        canManage ? (
          <Button size="sm" variant="danger" onClick={() => setConfirmDel(row.original)}>
            Revoke
          </Button>
        ) : null,
    },
  ]

  return (
    <div className="space-y-3">
      <Toolbar className="justify-end">
        {canManage ? (
          <Button onClick={() => { reset(); setOpen(true) }}>
            <Plus className="size-4" />
            Create API key
          </Button>
        ) : null}
      </Toolbar>
      <Card>
        <CardContent className="p-0">
          <DataTable
            columns={columns}
            rows={keys}
            loading={keysQ.isLoading}
            empty="No API keys yet."
          />
        </CardContent>
      </Card>

      <Modal
        open={open}
        onOpenChange={(v) => { setOpen(v); if (!v) reset() }}
        title={secret ? 'API key created' : 'Create API key'}
        footer={
          secret ? (
            <Button onClick={() => setOpen(false)}>Done</Button>
          ) : (
            <>
              <Button variant="secondary" onClick={() => setOpen(false)}>
                Cancel
              </Button>
              <Button onClick={submit} disabled={create.isPending}>
                Create
              </Button>
            </>
          )
        }
      >
        {secret ? (
          <div className="space-y-3">
            <Alert variant="destructive">
              <Key className="size-4" />
              <AlertDescription>
                Save this secret now - it will <strong>never</strong> be shown again.
              </AlertDescription>
            </Alert>
            <div className="flex items-stretch gap-1">
              <code className="flex-1 break-all rounded-md bg-muted p-2 font-mono text-xs">
                {secret}
              </code>
              <Button variant="outline" onClick={copySecret} aria-label="Copy">
                <Copy className="size-4" />
              </Button>
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            <FormField
              label="Key name"
              value={name}
              onChange={(e) => setName(e.currentTarget.value)}
              required
              placeholder="e.g. Production webhook"
            />
            {error ? (
              <Alert variant="destructive">
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            ) : null}
          </div>
        )}
      </Modal>

      <Modal.Confirm
        open={!!confirmDel}
        onOpenChange={(v) => !v && setConfirmDel(null)}
        title="Revoke API key"
        body={`Revoke "${confirmDel?.name ?? ''}"? Any integrations using this key will stop working immediately.`}
        confirmLabel="Revoke"
        confirmVariant="danger"
        onConfirm={async () => {
          const c = confirmDel
          setConfirmDel(null)
          if (c) await onDelete(c)
        }}
      />
    </div>
  )
}
